import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, CheckCircle, AlertTriangle, QrCode, Tablet, Loader2, TrendingUp } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [barangays, setBarangays] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, b, c, p] = await Promise.all([
          API.get('/dashboard/admin-stats'),
          API.get('/dashboard/barangay-breakdown'),
          API.get('/dashboard/candidate-stats'),
          API.get('/dashboard/surveyor-performance'),
        ]);
        setStats(s.data);
        setBarangays(b.data);
        setCandidates(c.data);
        setSurveyors(p.data);
      } catch (err) {
        console.error('Dashboard load error:', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const modeData = [
    { name: 'Self-Scan', value: stats?.self_scan_count || 0 },
    { name: 'Assisted', value: stats?.assisted_count || 0 },
  ];
  const MODE_COLORS = ['#3b82f6', '#f97316'];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Command Center</h1>
        <p className="text-slate-500 text-sm mt-1">Real-time survey analytics and field operations overview</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Total Voters</p>
                <p data-testid="stat-total-voters" className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_voters || 0}</p>
              </div>
              <Users className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Surveyed</p>
                <p data-testid="stat-surveyed" className="text-3xl font-bold text-emerald-600 mt-1">{stats?.total_surveyed || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Completion</p>
                <p data-testid="stat-completion" className="text-3xl font-bold text-blue-600 mt-1">{stats?.completion_pct || 0}%</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-slate-200">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Anomalies</p>
                <p data-testid="stat-anomalies" className="text-3xl font-bold text-rose-600 mt-1">{stats?.anomaly_count || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-rose-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Candidate Stats */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Candidate Standings</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={candidates.filter(c => c.candidate_id)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="candidate_name" type="category" width={120} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                  formatter={(value, name, props) => [`${value} votes (${props.payload.percentage}%)`, 'Count']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {candidates.filter(c => c.candidate_id).map((c, i) => (
                    <Cell key={i} fill={c.party_color || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Mode Breakdown */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Submission Mode</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={modeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4}>
                  {modeData.map((_, i) => <Cell key={i} fill={MODE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-2">
              <div className="flex items-center gap-2 text-sm">
                <QrCode className="w-4 h-4 text-blue-500" />
                <span className="text-slate-600">Self-Scan: {stats?.self_scan_count || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Tablet className="w-4 h-4 text-orange-500" />
                <span className="text-slate-600">Assisted: {stats?.assisted_count || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Precinct Completion */}
      <Card className="mb-6 border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Precinct Completion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="precinct-table">
              <thead>
                <tr>
                  <th>Precinct</th>
                  <th>Total</th>
                  <th>Surveyed</th>
                  <th>Progress</th>
                  <th>Leading Candidate</th>
                </tr>
              </thead>
              <tbody>
                {barangays.map((b) => (
                  <tr key={b.precinct_id || b.barangay_id}>
                    <td className="font-medium text-slate-900 font-mono">{b.precinct_number || b.barangay_name}</td>
                    <td className="text-right">{b.total_voters}</td>
                    <td className="text-right">{b.surveyed}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Progress value={b.completion_pct} className="h-2 flex-1" />
                        <span className="text-xs font-medium text-slate-500 w-12 text-right">{b.completion_pct}%</span>
                      </div>
                    </td>
                    <td>
                      {b.leading_candidate ? (
                        <Badge style={{ backgroundColor: b.leading_color || '#94a3b8', color: '#fff' }} className="text-xs">
                          {b.leading_candidate} ({b.leading_count})
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">No data</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Surveyor Performance */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Surveyor Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="surveyor-table">
              <thead>
                <tr>
                  <th>Surveyor</th>
                  <th>Team Leader</th>
                  <th>Assigned</th>
                  <th>Completed</th>
                  <th>Progress</th>
                  <th>Self-Scan</th>
                  <th>Assisted</th>
                </tr>
              </thead>
              <tbody>
                {surveyors.map((s) => (
                  <tr key={s.surveyor_id}>
                    <td className="font-medium text-slate-900">{s.surveyor_name}</td>
                    <td className="text-slate-600">{s.team_leader_name || '-'}</td>
                    <td className="text-right">{s.total_assigned}</td>
                    <td className="text-right">{s.completed}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Progress value={s.completion_pct} className="h-2 flex-1" />
                        <span className="text-xs font-medium text-slate-500 w-12 text-right">{s.completion_pct}%</span>
                      </div>
                    </td>
                    <td className="text-right text-blue-600">{s.self_scan}</td>
                    <td className="text-right text-orange-600">{s.assisted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
